import {
  UmsMember,
  UmsMemberMail,
  UmsMemberMailTemplate,
  UmsMemberOperateHistory,
  UmsMemberLoginLog,
  UmsMemberReceiveAddress,
  UmsMemberReceiveAddressV1,
  UmsMemberReceiveAddressV2,
  UmsMemberPointHistoryV2,
  UmsIntegralType,
  UmsIntegralPayload,
  UmsIntegralHistoryPayload,
  UmsIntegralEditPayload,
  UmsIntegralHistory,
  UmsStaff,
  StaffReserveHistory,
} from 'types/ums';
import request, { ginzaxiaomaApiRequest } from '.';
import { BaseRes, PageQuery, UnwrapPromise } from 'types/base';
import { useEffect, useState } from 'react';

export type GetMemberListDto = {
  createSource?: number;
  keyword?: string;
  memberLevel?: string;
} & PageQuery;

export const getMemberById = (id: UmsMember['id']) => {
  return request.get<BaseRes<UmsMember>>(`/ums/member/${id}`);
};

export const getMemberByIdV2 = (id: UmsMember['id']) => {
  return ginzaxiaomaApiRequest.get<{
    id: number;
    email: string;
    integration: number;
    username: string;
    phone: string;
    language?: 'en' | 'ja' | 'zh_CN' | 'zh_TW';
    country: string;
    createTime: string;
    createSource: number;
    firstName: string;
    lastName: string;
    status: number;
    /** 0 正常，1 已注销 */
    deleteStatus: number;
    memberLevelId: number;
    firstNameKatakana?: string;
    lastNameKatakana?: string;
    gender: number;
    birthday?: string | null;
    countryCode?: string;
    socialAccount?: string;
    socialName?: string;
    nickname?: string;
    city?: string;
    detailAddress?: string;
    pointExpireTime?: string;
    lastLoginTime?: string;
    memberLevel: string;
    tags?: string[];
    totalPayAmount: number;
    remark?: string;
    idCertificate?: string;
    paymentDetails?: string;
    last365DaysAmount?: number;
  }>(`/admin/umsMember/info/${id}`);
};

export const getMemberList = (params: GetMemberListDto) => {
  return request
    .get<
      BaseRes<{
        list: UmsMember[];
        total: number;
      }>
    >('/ums/member/list', { params })
    .then((res) => {
      res.data.list.forEach((i) => {
        const { firstName, lastName, phone, countryCode, email } = i;
        let showName = '';
        const isChineseOrJapanese = (text: string) =>
          /[\u4E00-\u9FFF\u3040-\u30FF]/.test(text);

        if (firstName && lastName) {
          if (isChineseOrJapanese(firstName) || isChineseOrJapanese(lastName)) {
            showName = `${lastName} ${firstName}`; // 中文或日文：姓在前，名在后
          } else {
            showName = `${firstName} ${lastName}`; // 英文：名在前，姓在后
          }
        } else {
          showName = email || '-';
        }

        i.showName = showName;
        i.showPhone = countryCode && phone ? `+${countryCode} ${phone}` : '-';
      });

      return res;
    });
};

export const getMemberListV2 = (
  dto: {
    keyword?: string;
    createSource?: number[];
    memberLevel?: string[];
    tags?: string[];
    memberId?: number;
    blocked?: boolean;
  } & PageQuery,
) => {
  return ginzaxiaomaApiRequest
    .post<{
      list: (UnwrapPromise<ReturnType<typeof getMemberByIdV2>> & {
        showName?: string;
        showPhone?: string;
        isBlocked: boolean;
      })[];
      total: number;
    }>('/admin/umsMember/list', dto)
    .then((res) => {
      res.list.forEach((i) => {
        const { firstName, lastName, nickname, phone, countryCode } = i;
        let showName = '';
        if (firstName && lastName) {
          showName = `${firstName} ${lastName}`;
        } else {
          showName = nickname || '-';
        }

        i.showName = showName;
        i.showPhone = countryCode && phone ? `+${countryCode} ${phone}` : '-';
      });

      return res;
    });
};

export const editUmsMemberTags = ({
  tags,
  id,
}: {
  tags: string[];
  id: number;
}) => {
  return ginzaxiaomaApiRequest.post(`/admin/umsMember/update-tags/${id}`, {
    tags,
  });
};

export const getUmsMemberTagOptions = () => {
  return ginzaxiaomaApiRequest.get<string[]>('/admin/umsMember/tag-options');
};

export const getMemberReceiveAddressListV1 = (memberId: UmsMember['id']) => {
  return request.get<BaseRes<UmsMemberReceiveAddressV1[]>>(
    `/member/address/default/member/${memberId}`,
  );
};

export const getMemberReceiveAddressList = (memberId: UmsMember['id']) => {
  return ginzaxiaomaApiRequest.post<UmsMemberReceiveAddressV2[]>(
    '/admin/umsMember/receive-address-list',
    { memberId },
  );
};

export const useGetMemberReceiveAddressList = (memberId: UmsMember['id']) => {
  const [loading, setLoading] = useState(false);
  const [memberReceiveAddressList, setMemberReceiveAddressList] = useState<
    UmsMemberReceiveAddressV2[]
  >([]);

  useEffect(() => {
    getAddressList(memberId);
  }, [memberId]);

  const getAddressList = (memberId: number) => {
    setLoading(true);
    getMemberReceiveAddressList(memberId)
      .then((list) => {
        setMemberReceiveAddressList(list);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return {
    loading,
    memberReceiveAddressList,
    getAddressList,
  };
};

/** Admin 端新增会员收货地址 */
export const addMemberReceiveAddressV2 = (data: {
  memberId: UmsMember['id'];
  name: string;
  phoneNumber: string;
  postCode: string;
  detailAddress: string;
  province: string;
  city?: string;
  defaultStatus: number;
}) => {
  return ginzaxiaomaApiRequest.post<{ data: number }>(
    '/admin/umsMember/receive-address/add',
    data,
  );
};

/** Admin 端修改会员收货地址，路径 id 为地址 id */
export const updateMemberReceiveAddressV2 = (
  id: number | string,
  data: {
    name: string;
    phoneNumber: string;
    postCode: string;
    detailAddress: string;
    province: string;
    city?: string;
    defaultStatus: number;
  },
) => {
  return ginzaxiaomaApiRequest.post(
    `/admin/umsMember/receive-address/update/${id}`,
    data,
  );
};

/** Admin 端删除会员收货地址 */
export const deleteMemberReceiveAddressV2 = (
  id: number | string,
  memberId: UmsMember['id'],
) => {
  return ginzaxiaomaApiRequest.post(
    `/admin/umsMember/receive-address/delete/${id}`,
    { memberId },
  );
};

export const umsMemberUpdate = (data: Partial<UmsMember>) => {
  return request.post<BaseRes<null>>('/ums/member/update', data);
};

export const umsMemberCreate = (data: Omit<UmsMember, 'id'>) => {
  return request.post<BaseRes<UmsMember>>('/ums/member/create', data);
};

export const umsMemberCreateAPI = (
  data: Pick<
    UmsMember,
    | 'email'
    | 'createSource'
    | 'firstName'
    | 'lastName'
    | 'firstNameKatakana'
    | 'lastNameKatakana'
    | 'gender'
    | 'language'
    | 'countryCode'
    | 'phone'
    | 'birthday'
    | 'country'
    | 'city'
    | 'detailAddress'
    | 'socialName'
    | 'socialAccount'
    | 'idCertificate'
    | 'paymentDetails'
    | 'remark'
  >,
) => {
  return ginzaxiaomaApiRequest.post<UmsMember>('/admin/umsMember/create', data);
};

export const getMemberPointRate = (memberId: number) => {
  return request.get<
    BaseRes<{
      isBirthdayMonth: boolean;
      pointRate: number;
    }>
  >('/ums/member-point-rate/getByMemberId', {
    params: {
      memberId,
    },
  });
};

// 积分变更接口（仅 admin 账号可用）
export const umsMemberPointUpdate = (data: {
  memberId: UmsMember['id'];
  type: number;
  point: number;
  operateNote: string;
}) => {
  return ginzaxiaomaApiRequest.post('/admin/umsMember/point/update', data);
};

/** Admin 端分页查询会员积分变动历史 */
export const getMemberPointHistoryDailyStats = (dto?: {
  days?: 7 | 14 | 30;
  timeZone?: string;
}) => {
  return ginzaxiaomaApiRequest.get<{
    days: 7 | 14 | 30;
    list: {
      date: string;
      issued: number;
      used: number;
    }[];
  }>('/admin/member-point-history/daily-stats', { params: dto });
};

export const getMemberPointHistoryList = (dto: {
  pageNum: number;
  pageSize: number;
  memberId?: number;
  memberKeyword?: string;
  direction?: 'grant' | 'use';
  operateType?: number;
  orderSn?: string;
  operateMan?: string;
  startTime?: string;
  endTime?: string;
  timeZone?: string;
}) => {
  return ginzaxiaomaApiRequest.get<{
    list: {
      id: number;
      memberId: number;
      memberEmail: string | null;
      memberUsername: string | null;
      orderId: number | null;
      orderSn: string | null;
      changeCount: number;
      operateType: number;
      operateMan: string | null;
      operateNote: string | null;
      createTime: string;
      pointMarketingId: number | null;
    }[];
    total: number;
  }>('/admin/member-point-history/list', { params: dto });
};

export const getMemberPointHistoryListV2 = (
  data: {
    memberId?: UmsMember['id'];
    orderId?: number;
  } & Partial<PageQuery>,
) => {
  return ginzaxiaomaApiRequest.post<{
    list: UmsMemberPointHistoryV2[];
    total: number;
  }>('/admin/umsMember/point-history', data);
};

// 回收寄卖预约管理
export const umsSearchMember = (keyword: string) => {
  return request.get<BaseRes<UmsMember>>(
    `/ums/member/umsMemberInfo?keyword=${keyword}`,
  );
};

// 增加用户地址
export const umsAddMemberAddress = (data: UmsMemberReceiveAddress) => {
  return request.post<BaseRes<string>>(`/member/address/add`, data);
};

export const umsMemberUpdateStatus = (
  data: Pick<UmsMember, 'id' | 'status'>,
) => {
  return request.post('ums/member/updateStatus', null, {
    params: data,
  });
};

export const umsMemberBlock = (id: number) => {
  return ginzaxiaomaApiRequest.post(`/admin/umsMember/block/${id}`);
};

export const umsMemberUnblock = (id: number) => {
  return ginzaxiaomaApiRequest.post(`/admin/umsMember/unblock/${id}`);
};

/** 管理员注销会员账号（仅 admin；有订单或回收寄卖订单时不可注销） */
export const umsMemberDeleteAccount = (id: number, note?: string) => {
  return ginzaxiaomaApiRequest.post(`/admin/umsMember/delete-account/${id}`, {
    note,
  });
};

/** 管理员修改会员邮箱（仅 admin；新邮箱被其他未注销会员占用时不可改） */
export const umsMemberChangeEmail = (
  id: number,
  email: string,
  note?: string,
) => {
  return ginzaxiaomaApiRequest.post(`/admin/umsMember/change-email/${id}`, {
    email,
    note,
  });
};

/** 手动刷新单个会员等级（按近 365 天已完成订单消费重算） */
export const updateMemberLevelOne = (data: {
  memberId: number;
  isSendEmail?: boolean;
  isGivePoints?: boolean;
}) => {
  return ginzaxiaomaApiRequest.post<{
    memberId: number;
    oldLevelCode: string;
    memberLevel: string;
    last365DaysAmount: number;
    levelChanged: boolean;
  }>('/admin/ums-member-level/update-member-level-one', data);
};

export const getMailTemplateList = (data: PageQuery) => {
  return request.post<
    BaseRes<{
      list: UmsMemberMailTemplate[];
      total: number;
    }>
  >('ums/member-mail/get-mail-template-list', data);
};

export const umsMemberMailTemplateSaveOrUpdate = (
  data: UmsMemberMailTemplate,
) => {
  return request.post('ums/member-mail/template/saveOrUpdate', data);
};

export const umsMemberSendCustomMail = (data: {
  memberId: UmsMember['id'];
  subject: string;
  mailContent: string;
  relatedType?: string;
  relatedId?: string;
}) => {
  return ginzaxiaomaApiRequest.post('/admin/common/send-custom-mail', data);
};

export const getIntegralList = (data?: UmsIntegralPayload) => {
  return ginzaxiaomaApiRequest.post<UmsIntegralType[]>(
    'admin/point-marketing/findAll',
    data,
  );
};

export const getIntegralDetail = (id: UmsIntegralType['id']) => {
  return ginzaxiaomaApiRequest.get<UmsIntegralType>(
    `admin/point-marketing/findOne/${id}`,
  );
};

export const getIntegralHistory = (
  data: PageQuery & UmsIntegralHistoryPayload,
) => {
  return ginzaxiaomaApiRequest.post<{
    list: UmsIntegralHistory[];
    total: number;
  }>(`admin/point-marketing/point-history-list`, data);
};

export const getIntegralEdit = (data: UmsIntegralEditPayload) => {
  return ginzaxiaomaApiRequest.post(`admin/point-marketing/addOrUpdate`, data);
};

export const getUmsMemberMailList = (
  data: PageQuery & { memberId?: number; orderId?: number },
) => {
  return ginzaxiaomaApiRequest.get<{
    list: UmsMemberMail[];
    total: number;
  }>(`admin/ums-member-email/list`, { params: data });
};

export const getUmsStaffList = (data: {
  storeId: number;
  pageNum: number;
  pageSize: number;
  name?: string;
}) => {
  return ginzaxiaomaApiRequest.post<UmsStaff[]>('/admin/staff/list', data);
};

export const upsertStaff = (
  data: Partial<
    Pick<
      UmsStaff,
      | 'id'
      | 'storeId'
      | 'name'
      | 'romanName'
      | 'description'
      | 'avatar'
      | 'enabled'
      | 'personalPhoto'
      | 'status'
      | 'languages'
      | 'sort'
      | 'appraisal'
      | 'regularVisit'
    >
  >,
) => {
  return ginzaxiaomaApiRequest.post<UmsStaff>(
    '/admin/staff/create-or-update',
    data,
  );
};

export const deleteStaff = (id: UmsStaff['id']) => {
  return ginzaxiaomaApiRequest.delete(`/admin/staff/${id}`);
};

export const reserveList = (data: StaffReserveHistory) => {
  return ginzaxiaomaApiRequest.post<StaffReserveHistory[]>(
    `admin/staff/reserve/list`,
    data,
  );
};

export const updateUmsMemberV2 = (
  id: UmsMember['id'],
  data: {
    firstName?: string;
    lastName?: string;
    firstNameKatakana?: string;
    lastNameKatakana?: string;
    countryCode?: string;
    phone?: string;
    gender?: number;
    birthday?: string | null;
    createSource?: number;
    socialName?: string;
    socialAccount?: string;
    country?: string;
    city?: string;
    detailAddress?: string;
    idCertificate?: string;
    paymentDetails?: string;
    remark?: string;
    language?: string;
  },
) => {
  return ginzaxiaomaApiRequest.post(`/admin/umsMember/update/${id}`, data);
};

export const umsMemberEmailExists = (email: string) => {
  return ginzaxiaomaApiRequest.post<{
    exist: boolean;
  }>(`/admin/umsMember/check-email-exist`, {
    email,
  });
};

export const getMemberCartList = ({
  memberId,
  pageNum,
  pageSize,
}: {
  memberId: UmsMember['id'];
  pageNum: number;
  pageSize: number;
}) => {
  return ginzaxiaomaApiRequest.post<{
    list: {
      id: number;
      createDate: string;
      product: {
        id: number;
        price: number;
        name: string;
        currency: string;
        stockPlace: string;
        stockStatus: string;
        publishStatus: number;
        stock: number;
        pic?: string;
      };
    }[];
    total: number;
  }>(`/admin/umsMember/cart-list`, { memberId, pageNum, pageSize });
};

export const getMemberLoginLogList = ({
  memberId,
  pageNum,
  pageSize,
}: {
  memberId: UmsMember['id'];
  pageNum: number;
  pageSize: number;
}) => {
  return ginzaxiaomaApiRequest.post<{
    list: UmsMemberLoginLog[];
    total: number;
  }>('/admin/umsMember/login-log', { memberId, pageNum, pageSize });
};

export const getMemberOperateHistoryList = ({
  memberId,
  pageNum,
  pageSize,
}: {
  memberId: UmsMember['id'];
  pageNum: number;
  pageSize: number;
}) => {
  return ginzaxiaomaApiRequest.post<{
    list: UmsMemberOperateHistory[];
    total: number;
  }>('/admin/umsMember/operate-history', { memberId, pageNum, pageSize });
};

export const getMemberCollectionList = ({
  memberId,
  pageNum,
  pageSize,
}: {
  memberId: UmsMember['id'];
  pageNum: number;
  pageSize: number;
}) => {
  return ginzaxiaomaApiRequest.post<{
    list: {
      id: string;
      createTime: string;
      product: {
        id: number;
        price: number;
        name: string;
        currency: string;
        stockPlace: string;
        stockStatus: string;
        publishStatus: number;
        stock: number;
        pic?: string;
      };
    }[];
    total: number;
  }>(`/admin/umsMember/collection-list`, { memberId, pageNum, pageSize });
};
